import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { money, formatApiError, todayISO } from "@/lib/api";
import { DOC_TYPES } from "@/lib/constants";
import { useApp } from "@/context/AppContext";
import { PageHeader, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + Number(n || 0));
  return d.toISOString().slice(0, 10);
};

const blankItem = () => ({ description: "", quantity: 1, unit_price: 0 });

function ItemsTable({ items, setItems, sym }) {
  const upd = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-2" data-testid="items-table">
      <div className="hidden sm:grid grid-cols-[1fr_90px_130px_120px_40px] gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500 px-1">
        <span>Concepto</span><span>Cant.</span><span>P. unitario</span><span className="text-right">Importe</span><span />
      </div>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_90px_130px_120px_40px] gap-2 items-center">
          <Input className="col-span-2 sm:col-span-1" placeholder="Ej. Closet de MDF 2.40 x 1.80 m" value={it.description} onChange={(e) => upd(i, "description", e.target.value)} data-testid={`item-description-${i}`} />
          <Input type="number" step="any" min="0" value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} data-testid={`item-quantity-${i}`} />
          <Input type="number" step="any" min="0" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} data-testid={`item-price-${i}`} />
          <span className="text-right font-mono text-sm">{money(Number(it.quantity) * Number(it.unit_price), sym)}</span>
          <Button type="button" variant="ghost" size="icon" disabled={items.length === 1} onClick={() => setItems(items.filter((_, idx) => idx !== i))} data-testid={`item-remove-${i}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, blankItem()])} data-testid="add-item-button">
        <Plus className="h-4 w-4 mr-1" />Agregar concepto
      </Button>
    </div>
  );
}

function Totals({ f, sym, taxName }) {
  const subtotal = f.items.reduce((a, i) => a + Number(i.quantity) * Number(i.unit_price), 0);
  const taxable = Math.max(subtotal - Number(f.discount || 0), 0);
  const tax = (taxable * Number(f.tax_rate || 0)) / 100;
  const total = taxable + tax;
  const Row = ({ l, v, id, bold }) => (
    <div className={`flex justify-between ${bold ? "text-lg font-bold border-t pt-2" : "text-sm"}`}>
      <span>{l}</span><span className="font-mono" data-testid={id}>{v}</span>
    </div>
  );
  return (
    <div className="space-y-2">
      <Row l="Subtotal" v={money(subtotal, sym)} id="total-subtotal" />
      <Row l="Descuento" v={`-${money(f.discount, sym)}`} id="total-discount" />
      <Row l={`${taxName} (${f.tax_rate}%)`} v={money(tax, sym)} id="total-tax" />
      <Row l="Total" v={money(total, sym)} id="total-grand" bold />
      {f.advance > 0 && <Row l="Saldo" v={money(total - Number(f.advance), sym)} id="total-balance" />}
    </div>
  );
}

export default function DocumentEditor({ type }) {
  const cfg = DOC_TYPES[type];
  const { id } = useParams();
  const nav = useNavigate();
  const { settings } = useApp();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const sym = settings?.currency_symbol || "$";

  useEffect(() => {
    if (id) {
      api.get(`${cfg.endpoint}/${id}`).then((r) => setF(r.data));
    } else if (settings) {
      const date = todayISO();
      setF({
        client_name: "", client_phone: "", client_email: "", client_address: "", client_rfc: "",
        date, valid_until: type === "quote" ? addDays(date, settings.quote_validity_days) : "",
        items: [blankItem()], discount: 0, tax_rate: settings.tax_rate, advance: 0,
        status: cfg.defaultStatus, notes: "",
      });
    }
  }, [id, settings, cfg, type]);

  if (!f) return <p className="text-stone-500">Cargando…</p>;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    const items = f.items.filter((i) => i.description.trim()).map((i) => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) }));
    if (!items.length) return toast.error("Agrega al menos un concepto con descripción");
    setSaving(true);
    const body = { ...f, items, discount: Number(f.discount || 0), tax_rate: Number(f.tax_rate || 0), advance: Number(f.advance || 0) };
    try {
      const { data } = id ? await api.put(`${cfg.endpoint}/${id}`, body) : await api.post(cfg.endpoint, body);
      toast.success(`${cfg.single} guardada`);
      nav(`${cfg.base}/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} data-testid={`${type}-editor-form`}>
      <PageHeader title={id ? `Editar ${cfg.single.toLowerCase()} ${f.folio}` : `Nueva ${cfg.single.toLowerCase()}`}>
        <Button type="button" variant="outline" onClick={() => nav(-1)} data-testid="editor-cancel-button">Cancelar</Button>
        <Button type="submit" disabled={saving} className="bg-amber-700 hover:bg-amber-800" data-testid="editor-save-button">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Guardar
        </Button>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <section className="rounded-xl border bg-white p-5 grid gap-4 sm:grid-cols-2">
            <h3 className="sm:col-span-2 font-bold">Datos del cliente</h3>
            <Field label="Nombre *"><Input required value={f.client_name} onChange={set("client_name")} data-testid="client-name-input" /></Field>
            <Field label="Teléfono"><Input value={f.client_phone} onChange={set("client_phone")} data-testid="client-phone-input" /></Field>
            <Field label="Correo"><Input type="email" value={f.client_email} onChange={set("client_email")} data-testid="client-email-input" /></Field>
            <Field label="RFC / ID fiscal"><Input value={f.client_rfc} onChange={set("client_rfc")} data-testid="client-rfc-input" /></Field>
            <Field label="Dirección" className="sm:col-span-2"><Input value={f.client_address} onChange={set("client_address")} data-testid="client-address-input" /></Field>
          </section>
          <section className="rounded-xl border bg-white p-5 space-y-4">
            <h3 className="font-bold">Conceptos</h3>
            <ItemsTable items={f.items} setItems={(items) => setF({ ...f, items })} sym={sym} />
          </section>
          <section className="rounded-xl border bg-white p-5">
            <Field label="Notas / condiciones"><Textarea rows={3} value={f.notes} onChange={set("notes")} placeholder="Tiempo de entrega, acabados, condiciones de pago…" data-testid="notes-input" /></Field>
          </section>
        </div>
        <aside className="space-y-6">
          <section className="rounded-xl border bg-white p-5 space-y-4">
            <Field label="Fecha"><Input type="date" required value={f.date} onChange={set("date")} data-testid="date-input" /></Field>
            {type === "quote" && <Field label="Válida hasta"><Input type="date" value={f.valid_until} onChange={set("valid_until")} data-testid="valid-until-input" /></Field>}
            <Field label="Estado">
              <Select value={f.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{cfg.statuses.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={`Descuento (${sym})`}><Input type="number" step="any" min="0" value={f.discount} onChange={set("discount")} data-testid="discount-input" /></Field>
              <Field label={`${settings?.tax_name || "IVA"} %`}><Input type="number" step="any" min="0" value={f.tax_rate} onChange={set("tax_rate")} data-testid="tax-rate-input" /></Field>
            </div>
            {type === "sale" && <Field label={`Anticipo (${sym})`}><Input type="number" step="any" min="0" value={f.advance} onChange={set("advance")} data-testid="advance-input" /></Field>}
          </section>
          <section className="rounded-xl border-2 border-amber-200 bg-amber-50/40 p-5">
            <Totals f={f} sym={sym} taxName={settings?.tax_name || "IVA"} />
          </section>
        </aside>
      </div>
    </form>
  );
}
